import { ImageSize, cn } from '@theprintbar/shared';
import clsx from 'clsx';
import { HTMLAttributes, MutableRefObject, useEffect, useRef, useState } from 'react';

export interface ImageContainerProps extends HTMLAttributes<HTMLDivElement> {
	image?: {
		altText?: string | null;
		image?: {
			url: string;
		} | null;
	} | null;
	mainImageURL?: string;
	crop?: boolean;
	contain?: boolean;
	cropPadding?: number;
	size?: ImageSize;
	lazyLoad?: boolean;
}

export function ImageContainer({
	image,
	lazyLoad = true,
	size = ImageSize.Large,
	mainImageURL,
	className,
	contain,
	crop,
	cropPadding = 0,
	...divProps
}: ImageContainerProps) {
	const imgRef = useRef<HTMLImageElement | null>(null);
	const [croppedImage, setCropped] = useState('');
	let imageUrl = image?.image?.url || mainImageURL;
	if (!imageUrl) return null;

	//Add size param to url
	const url = new URL(imageUrl);
	url.searchParams.set('size', size.toString());

	useEffect(() => {
		if (!crop) return;
		getCroppedImage({ imageRef: imgRef, cropPadding, setCropped });
	}, [crop, image]);

	return (
		<div className={clsx('overflow-hidden relative', className)} {...divProps}>
			<img
				ref={imgRef}
				src={url.toString()}
				alt={image?.altText || mainImageURL || ''}
				className={cn('h-full w-full absolute', crop && 'hidden', contain ? 'object-contain' : 'object-cover')}
				loading={lazyLoad ? 'lazy' : 'eager'}
			/>
			{croppedImage && <img src={croppedImage} className="object-cover h-full w-full absolute" />}
		</div>
	);
}

interface GetCroppedImageProps {
	imageRef: MutableRefObject<HTMLImageElement | null>;
	cropPadding: number;
	setCropped: (src: string) => void;
}
function getCroppedImage({ imageRef, cropPadding, setCropped }: GetCroppedImageProps) {
	if (!imageRef.current) return;

	const image = imageRef.current;
	const canvas = document.createElement('canvas');
	const ctx = canvas.getContext('2d');

	if (!ctx) return;

	image.onload = () => {
		canvas.width = image.width;
		canvas.height = image.height;

		ctx.drawImage(image, 0, 0);

		const imageData = ctx.getImageData(0, 0, image.width, image.height);
		const { data, width, height } = imageData;

		let top = height,
			left = width,
			right = 0,
			bottom = 0;

		// Detect non-transparent pixels
		for (let y = 0; y < height; y++) {
			for (let x = 0; x < width; x++) {
				const index = (y * width + x) * 4;
				const alpha = data[index + 3];

				if (alpha > 0) {
					if (x < left) left = x;
					if (x > right) right = x;
					if (y < top) top = y;
					if (y > bottom) bottom = y;
				}
			}
		}

		const croppedWidth = right - left + 1;
		const croppedHeight = bottom - top + 1;

		// Apply padding
		const paddingX = Math.floor(croppedWidth * cropPadding);
		const paddingY = Math.floor(croppedHeight * cropPadding);

		const paddedLeft = Math.max(left - paddingX, 0);
		const paddedRight = Math.min(right + paddingX, width - 1);
		const paddedTop = Math.max(top - paddingY, 0);
		const paddedBottom = Math.min(bottom + paddingY, height - 1);

		const paddedWidth = paddedRight - paddedLeft + 1;
		const paddedHeight = paddedBottom - paddedTop + 1;

		// Maintain original aspect ratio
		const aspectRatio = width / height;

		// Adjust the crop area to match the original aspect ratio
		let finalWidth = paddedWidth;
		let finalHeight = paddedHeight;

		if (finalWidth / finalHeight > aspectRatio) {
			finalWidth = Math.floor(finalHeight * aspectRatio);
		} else {
			finalHeight = Math.floor(finalWidth / aspectRatio);
		}

		// Center the cropped area within the original dimensions
		const offsetX = Math.max(Math.floor((paddedWidth - finalWidth) / 2), 0);
		const offsetY = Math.max(Math.floor((paddedHeight - finalHeight) / 2), 0);

		const croppedCanvas = document.createElement('canvas');
		croppedCanvas.width = width; // maintain the original width for the canvas
		croppedCanvas.height = height; // maintain the original height for the canvas

		const croppedCtx = croppedCanvas.getContext('2d');
		if (croppedCtx) {
			croppedCtx.clearRect(0, 0, width, height); // Clear any previous content
			croppedCtx.drawImage(
				image,
				paddedLeft + offsetX,
				paddedTop + offsetY,
				finalWidth,
				finalHeight,
				0,
				0,
				width, // Scale to the original width
				height, // Scale to the original height
			);
			const croppedDataUrl = croppedCanvas.toDataURL();
			setCropped(croppedDataUrl);
		}
	};
}
